import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MoreVertOutlined } from '@mui/icons-material';
import { useUserIdentity } from '../UserMenu/userIdentity';
import { UserAvatar, UserAccountMenu } from '../UserMenu/UserAccountMenu';

interface Props {
  collapsed: boolean;
}

const Root = styled('div')(({ theme }) => ({
  height: 48,
  display: 'flex',
  alignItems: 'center',
  padding: '0 8px',
  boxSizing: 'border-box',
  borderTop: `1px solid ${theme.tokens.sidebarBorder}`,
  flexShrink: 0,
}));

const Pill = styled('button')<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: collapsed ? '6px 0' : '6px 8px',
  justifyContent: collapsed ? 'center' : 'flex-start',
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  fontFamily: 'inherit',
  textAlign: 'left',
  color: theme.tokens.fgPrimary,
  '&:hover': { backgroundColor: theme.tokens.surfaceSubtle },
  '&:focus-visible': {
    outline: `2px solid ${theme.tokens.accent}`,
    outlineOffset: 2,
  },
}));

const UserInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
});

export default function SidebarUserMenu({ collapsed }: Props) {
  const identity = useUserIdentity();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!identity) return null;

  const pill = (
    <Pill
      collapsed={collapsed}
      onClick={e => setAnchorEl(e.currentTarget)}
      aria-haspopup="true"
      aria-expanded={open}
    >
      <UserAvatar identity={identity} />
      {!collapsed && (
        <>
          <UserInfo>
            <Typography variant="body2" fontWeight={600} noWrap>
              {identity.name}
            </Typography>
          </UserInfo>
          <MoreVertOutlined
            sx={{ ml: 'auto', fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
          />
        </>
      )}
    </Pill>
  );

  return (
    <Root>
      {collapsed ? (
        <Tooltip title={identity.name} placement="right">
          {pill}
        </Tooltip>
      ) : (
        pill
      )}

      <UserAccountMenu
        identity={identity}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: collapsed ? 'right' : 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: collapsed ? 'left' : 'center' }}
      />
    </Root>
  );
}
